/**
 * CoConsoleXLayout.jsx
 * ---------------------------------------------------------------------------
 * Wraps every CoConsoleX subtab. Renders:
 *   1. Hero band (ink-navy, cream type, watermark privilege bubble)
 *   2. Sticky left rail nav + content area (responsive: pill bar on mobile)
 *   3. Inter-page nav strip (Previous / Next)
 *   4. Persistent CTA band linking to firms form with pre-selected interest
 */

import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { cxTokens as t, SUBTABS } from "../../lib/coconsolexTokens.js";
import CoConsoleXNav from "./CoConsoleXNav.jsx";

export default function CoConsoleXLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const idx = Math.max(
    0,
    SUBTABS.findIndex((s) => s.path === pathname),
  );
  const current = SUBTABS[idx];
  const prev = idx > 0 ? SUBTABS[idx - 1] : null;
  const next = idx < SUBTABS.length - 1 ? SUBTABS[idx + 1] : null;

  // Scroll content area to top on subtab change.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  const goEngage = () => {
    // Slug for Overview is intentionally "" (matches SUBTABS + server allow-list).
    const slug = current?.slug ?? "";
    navigate(`/?interest=coconsolex&from=${slug}#firms`);
  };

  return (
    <div style={{ background: t.cream, color: t.ink, paddingTop: 64 }}>
      {/* Hero band */}
      <div
        style={{
          background: t.ink,
          color: t.cream,
          padding: "56px 32px 48px",
          position: "relative",
          overflow: "hidden",
          minHeight: 220,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%", position: "relative", zIndex: 2 }}>
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              fontSize: 13,
              color: "#A3BFFA",
              letterSpacing: "0.22em",
              marginBottom: 14,
              textTransform: "uppercase",
            }}
          >
            From CaseSignalHQ
          </div>
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 700,
              fontSize: "clamp(36px, 5vw, 56px)",
              margin: 0,
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
            }}
          >
            CoConsole<span style={{ color: "#A3BFFA", fontWeight: 800 }}>X</span>
          </h1>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 16,
              maxWidth: 620,
              marginTop: 14,
              color: "rgba(250,247,240,0.85)",
              lineHeight: 1.55,
            }}
          >
            A Confidential AI Co-Counsel Service for the Legal Profession.
          </p>
        </div>

        {/* Faint watermark privilege bubble */}
        <svg
          aria-hidden
          viewBox="0 0 400 280"
          style={{
            position: "absolute",
            right: -60,
            top: 0,
            width: 420,
            height: 280,
            opacity: 0.18,
            pointerEvents: "none",
          }}
        >
          <ellipse
            cx="200"
            cy="140"
            rx="170"
            ry="110"
            fill="none"
            stroke={t.privilegeGold}
            strokeWidth="2"
          />
          <ellipse
            cx="200"
            cy="140"
            rx="140"
            ry="88"
            fill="none"
            stroke={t.privilegeGold}
            strokeWidth="1"
            strokeDasharray="3 4"
          />
        </svg>
      </div>

      {/* Mobile pill bar (shown < 900px via CSS) */}
      <div className="cx-mobile-pills" style={{ display: "none" }}>
        <div
          style={{
            display: "flex",
            gap: 6,
            padding: "12px 16px",
            overflowX: "auto",
            background: t.paper,
            borderBottom: `1px solid ${t.rule}`,
            position: "sticky",
            top: 64,
            zIndex: 50,
          }}
        >
          {SUBTABS.map((tab) => {
            const active = pathname === tab.path;
            return (
              <Link
                key={tab.path}
                to={tab.path}
                data-active={active}
                style={{
                  flexShrink: 0,
                  padding: "10px 14px",
                  border: `1px solid ${active ? t.ink : t.rule}`,
                  background: active ? t.navTint : t.paper,
                  textDecoration: "none",
                  color: t.ink,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 13,
                  fontWeight: active ? 500 : 400,
                  borderBottom: active ? `3px solid ${t.ink}` : `1px solid ${t.rule}`,
                  whiteSpace: "nowrap",
                  minHeight: 44,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    color: t.slate,
                    fontSize: 12,
                    letterSpacing: "0.18em",
                  }}
                >
                  {tab.roman}
                </span>
                {tab.title}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Body: rail + content */}
      <div
        className="cx-body"
        style={{
          display: "grid",
          gridTemplateColumns: "260px 1fr",
          maxWidth: 1200,
          margin: "0 auto",
          minHeight: "60vh",
          background: t.cream,
        }}
      >
        <aside
          className="cx-rail"
          style={{
            position: "sticky",
            top: 64,
            alignSelf: "start",
            maxHeight: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          <CoConsoleXNav />
        </aside>

        <main
          className="cx-content"
          style={{
            padding: "48px 56px 64px",
            maxWidth: 820,
          }}
        >
          <Outlet />

          {/* Inter-page nav strip */}
          <div
            style={{
              marginTop: 64,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            {prev ? (
              <Link
                to={prev.path}
                style={{
                  border: `1px solid ${t.rule}`,
                  padding: "20px 22px",
                  textDecoration: "none",
                  color: t.ink,
                  background: t.paper,
                  display: "block",
                  minHeight: 80,
                }}
              >
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    color: t.slate,
                    textTransform: "uppercase",
                  }}
                >
                  ← Previous
                </div>
                <div
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    marginTop: 6,
                  }}
                >
                  <em style={{ color: t.slate, fontSize: 16, marginRight: 8 }}>
                    {prev.roman}
                  </em>
                  {prev.title}
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link
                to={next.path}
                style={{
                  border: `1px solid ${t.rule}`,
                  padding: "20px 22px",
                  textDecoration: "none",
                  color: t.ink,
                  background: t.paper,
                  display: "block",
                  textAlign: "right",
                  minHeight: 80,
                }}
              >
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    color: t.slate,
                    textTransform: "uppercase",
                  }}
                >
                  Next →
                </div>
                <div
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    marginTop: 6,
                  }}
                >
                  <em style={{ color: t.slate, fontSize: 16, marginRight: 8 }}>
                    {next.roman}
                  </em>
                  {next.title}
                </div>
              </Link>
            ) : (
              <div />
            )}
          </div>
        </main>
      </div>

      {/* Persistent CTA band */}
      <div
        style={{
          background: t.cream,
          borderTop: `1px solid ${t.rule}`,
          borderBottom: `1px solid ${t.rule}`,
          padding: "32px 32px",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 24,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 22,
              color: t.ink,
              letterSpacing: "-0.01em",
            }}
          >
            Engage CoConsoleX as Co-Counsel.
          </div>
          <button
            onClick={goEngage}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              color: "#fff",
              background: t.ink,
              border: "none",
              padding: "16px 28px",
              cursor: "pointer",
              letterSpacing: "0.06em",
              minHeight: 48,
            }}
          >
            Request Engagement Letter
          </button>
        </div>
      </div>
    </div>
  );
}
